import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  IconButton,
  Paper,
  Avatar,
  Tooltip,
  Badge,
  Chip,
  Autocomplete,
  Checkbox,
} from '@material-ui/core'
import { LoadingButton } from '@material-ui/lab'
import { 
  Close as CloseIcon, 
  AttachFile,
  Send as SendIcon,
  Mail as MailIcon,
} from '@material-ui/icons'
import { PictureAsPdf } from '@material-ui/icons'
import { useSnackbar } from 'notistack'
import axios from 'axios'
import { currentFacilityId, REST_API_END_POINT } from 'src/constants/Defaultvalues'
import Icon from '@iconify/react'
import filePdfIcon from '@iconify/icons-mdi/file-pdf'
import imageIcon from '@iconify/icons-eva/image-fill'
import { useTheme, alpha } from '@material-ui/core/styles'
import AttachmentDownloadAllButton from 'src/components/_dashboard/official-request/AttachmentDownloadAllButton'
import {
  downloadAllAttachments,
  getAttachmentDownloadUrl,
  resolveDownloadFileName,
} from 'src/Helpers/FileDownloadUtils'

export default function SendDesignsEmailModal({
  open,
  onClose,
  selectedDesigns = [],
  designOrderId,
  requestId,
  quotationId,
  purchaseOrderId,
  userId,
  invoice = {},
  userType,
  onSuccess,
  pageType = 'design', // 'request', 'quotation', 'purchase_order', or 'design'
  installationId,
  installationOrderNumber,
  projectName,
  teamNames,
  clientName,
  installNote,
  isFromSplitEstimate = false,
  clientProfilePic = '',
  clientEmail = '',
  canChangeSender = false,
  installationName,
  installationJobName,
  details = {},
  pdfs = [],
  REST_API = '',
  loading = false,
  hasViewCostPermission = true,
}) {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const [recipientEmail, setRecipientEmail] = useState('')
  const [ccEmails, setCcEmails] = useState([])
  const [ccInputValue, setCcInputValue] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [ccError, setCcError] = useState('')
  const [subjectError, setSubjectError] = useState('')
  const [emailOptions, setEmailOptions] = useState([])
  const [loadingEmails, setLoadingEmails] = useState(false)
  const [senderEmail, setSenderEmail] = useState('')
  const [senderName, setSenderName] = useState('')
  const [showSenderField, setShowSenderField] = useState(false)
  const [selectedDownloadKeys, setSelectedDownloadKeys] = useState([])
  const [downloadSelectMode, setDownloadSelectMode] = useState(false)

  const getAttachmentKey = (file, index) =>
    String(file?.id || file?.url || file?.name || file?.preview || `attachment-${index}`)

  const isAttachmentDownloadable = (file) =>
    Boolean(getAttachmentDownloadUrl(file) || file?.invoiceData)

  const attachmentKeys = useMemo(
    () => (selectedDesigns || []).map((file, index) => getAttachmentKey(file, index)),
    [selectedDesigns],
  )

  useEffect(() => {
    if (!open) {
      setSelectedDownloadKeys([])
      setDownloadSelectMode(false)
    }
  }, [open])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

   // Helper function to get initials from name/email
   const getInitials = (label, email) => {
    if (label && label !== email) {
      const parts = label.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return label[0]?.toUpperCase() || email[0]?.toUpperCase() || '?'
    }
    return email[0]?.toUpperCase() || '?'
  }

  // Fetch staff members and clients emails
  useEffect(() => {
    if (open) {
      fetchEmailOptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

   // Auto-populate recipient email with client email when opened from approve flow
   useEffect(() => {
    if (open && canChangeSender && clientEmail) {
      setRecipientEmail(clientEmail)
      setEmailError('')
    }
  }, [open, canChangeSender, clientEmail])

  const isProposal =
    (currentFacilityId === 3) &&
    !(
      ((details?.is_request_estimate == 1 &&
        details?.hold == 0 &&
        details?.estimate_job_status == 3 &&
        details?.quotation_status == 3) ||
        (details?.is_request_estimate == 0 &&
          details?.quotation_status != 4)) &&
      details?.send_to_client == 0
    );

  const fetchEmailOptions = async () => {
    setLoadingEmails(true)
    try {
      const [staffResponse, clientsResponse, emailsResponse] = await Promise.all([
        axios.post(
          `${REST_API_END_POINT}project/get-all-team-members-for-email`, 
          {
            userId: userId,
            userType: userType,
          }
        ),	
        axios.post(`${REST_API_END_POINT}clients/getAllClients`, {
          userId: userId,
          userType: userType, 
          page: 1,
          rowsPerPage: 1000,
        }),
          axios.post(`${REST_API_END_POINT}email/get-emails`),
      ])

      const options = []

     // Add all staff members (team members) regardless of split estimate
     if (staffResponse.data?.status === 1 && staffResponse.data?.teamMembers) {
      staffResponse.data.teamMembers.forEach((staff) => {
        if (staff.email) {
          const name = staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email;
          options.push({
            email: staff.email,
            label: name,
            type: 'Staff',
            profilePic: staff.profile_pic || staff.userProfilePic || null,
          })
        }
      })
    }
      // Add clients
      if (clientsResponse.data?.status === 1 && clientsResponse.data?.customer) {
        clientsResponse.data.customer.forEach((client) => {
          if (client.email) {
            options.push({
              email: client.email,
              label: `${client.name || ''} ${client.last_name || ''}`.trim() || client.email,
              type: 'Client',
            })
          }
        })
      }

      // Add emails from get-emails API (always include in To and CC options)
      if (emailsResponse.data?.status === 1 && Array.isArray(emailsResponse.data?.emails)) {
        emailsResponse.data.emails.forEach((item) => {
          const email = item?.email || (typeof item === 'string' ? item : null)
          if (email) {
            options.push({
              email,
              label: email,
              type: 'Email',
            })
          }
        })
      }

      // Remove duplicates based on email, keeping the first occurrence
      const uniqueOptions = options.reduce((acc, current) => {
        const x = acc.find((item) => item.email === current.email)
        if (!x) {
          return acc.concat([current])
        } else {
          // If duplicate found, merge profile pic if current has one but existing doesn't
          if (current.profilePic && !x.profilePic) {
            x.profilePic = current.profilePic
          }
          return acc
        }
      }, [])

      // Add client profile pic to matching client email if provided
      if (canChangeSender && clientEmail && clientProfilePic) {
        const clientOption = uniqueOptions.find(opt => opt.email === clientEmail && opt.type === 'Client')
        if (clientOption) {
          clientOption.profilePic = clientProfilePic
        } else {
          // If client email not in options, add it
          uniqueOptions.push({
            email: clientEmail,
            label: clientEmail,
            type: 'Client',
            profilePic: clientProfilePic,
          })
        }
      }

      setEmailOptions(uniqueOptions)
    } catch (error) {
      console.error('Error fetching email options:', error)
      setEmailOptions([])
    } finally {
      setLoadingEmails(false)
    }
  }

  const handleEmailChange = (event, newValue, reason) => {
    // Only handle when an option is selected, not on input or clear
    if (reason === 'selectOption' && newValue) {
      let email = ''
      if (typeof newValue === 'string') {
        email = newValue
      } else if (newValue && newValue.email) {
        email = newValue.email
      }
      setRecipientEmail(email)
      if (email && !validateEmail(email)) {
        setEmailError('Please enter a valid email address')
      } else {
        setEmailError('')
      }
    } else if (reason === 'clear') {
      setRecipientEmail('')
      setEmailError('')
    }
  }

  const handleSubjectChange = (e) => {
    const value = e.target.value
    setSubject(value)
    if (value.trim() === '') {
      setSubjectError('Subject is required')
    } else {
      setSubjectError('')
    }
  }

  const handleCcInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      e.stopPropagation()
      addCcEmail()
    } else if (e.key === 'Backspace' && ccInputValue === '' && ccEmails.length > 0) {
      e.preventDefault()
      e.stopPropagation()
      // Remove last email on backspace if input is empty
      removeCcEmail(ccEmails.length - 1)
    }
  }

  const addCcEmail = (emailToAdd = null) => {
    const email = (emailToAdd || ccInputValue).trim()
    if (!email) return

    // Check if email is already added
    if (ccEmails.includes(email)) {
      setCcError('Email already added')
      return
    }

    // Validate email
    if (!validateEmail(email)) {
      setCcError('Please enter a valid email address')
      return
    }

    setCcEmails([...ccEmails, email])
    setCcInputValue('')
    setCcError('')
  }

  const removeCcEmail = (index) => {
    setCcEmails(ccEmails.filter((_, i) => i !== index))
    setCcError('')
  }

  const handleCcInputChange = (e) => {
    const value = e.target.value
    setCcInputValue(value)
    if (ccError) {
      setCcError('')
    }
  }

  const handleCcInputBlur = () => {
    if (ccInputValue.trim()) {
      addCcEmail()
    }
  }

  const handleSend = async () => {
    // Validate all fields at once and show all errors
    let hasErrors = false

    // Validate recipient email
    if (!recipientEmail.trim()) {
      setEmailError('Recipient email is required')
      hasErrors = true
    } else if (!validateEmail(recipientEmail)) {
      setEmailError('Please enter a valid email address')
      hasErrors = true
    } else {
      setEmailError('')
    }

    // Validate subject
    if (!subject.trim()) {
      setSubjectError('Subject is required')
      hasErrors = true
    } else {
      setSubjectError('')
    }

    // Validate "From" only for Approve flow (when sender change is enabled)
    if (canChangeSender && showSenderField) {
      if (!senderEmail.trim()) {
        hasErrors = true
        enqueueSnackbar('Please select a sender for this email', { variant: 'warning' })
      }
    }

    // Validate designs
    if ((selectedDesigns || []).length === 0) {
      hasErrors = true
    }

    // If there are any errors, show snackbar and return
    if (hasErrors) {
      if (!recipientEmail.trim()) {
        enqueueSnackbar('Please enter recipient email', { variant: 'warning' })
      } else if (!validateEmail(recipientEmail)) {
        enqueueSnackbar('Please enter a valid email address', { variant: 'warning' })
      } else if (!subject.trim()) {
        enqueueSnackbar('Please enter email subject', { variant: 'warning' })
      } else if ((selectedDesigns || []).length === 0) {
        enqueueSnackbar(
          pageType === 'installation_report'
            ? 'No installation reports selected'
            : 'No designs selected',
          { variant: 'warning' }
        )
      }
      return
    }

    try {
      setIsSending(true)
      
      const requestPayload = {
        selectedDesignIds: (selectedDesigns || []).map((d) => d.id),
        designs: selectedDesigns || [],
        recipientEmail: recipientEmail.trim(),
        ccEmails: ccEmails,
        subject: subject.trim(),
        message: message.trim(),
        isFromSplitEstimate: isFromSplitEstimate,
        details: details,
        invoice: invoice,
        pdfs: pdfs,
        REST_API: REST_API,
        loading: loading,
        hasViewCostPermission: hasViewCostPermission,
      }

      // Add sender info only when allowed and valid (used mainly for invoice approval flow)
      if (canChangeSender && senderEmail && validateEmail(senderEmail)) {
        requestPayload.senderEmail = senderEmail.trim()
        if (senderName && senderName.trim()) {
          requestPayload.senderName = senderName.trim()
        }
      }

      // Add ID based on pageType
      if ((pageType === 'request' || pageType === 'product_formula') && requestId) {
        requestPayload.request_id = requestId
      } else if (pageType !== 'quotation' && designOrderId) {
        requestPayload.design_order_id = designOrderId
        // // include request_id for design orders so backend can attach related agreements
        // if (details?.request_id) {
        //   requestPayload.request_id = details.request_id
        // }
      } else if (pageType === 'quotation' && quotationId) {
        requestPayload.quotation_id = quotationId
      } else if (pageType === 'purchase_order' && purchaseOrderId) {
        requestPayload.purchase_order_id = purchaseOrderId
      }

      // Add userId and userType if provided
      if (userId) {
        requestPayload.userId = userId
      }
      if (userType) {
        requestPayload.userType = userType
      }

      // Add pageType to identify the source
      requestPayload.pageType = pageType
      let res
      if (pageType === 'installation_report') {
        const installationPayload = {
          installation_id: installationId,
          order_number: installationOrderNumber,
          projectName,
          clientName,
          installNote,
          teamNames,
          installationJobName,
          reports: selectedDesigns || [],
          selectedReportIds: (selectedDesigns || []).map((report) => report.id),
          recipientEmail: recipientEmail.trim(),
          ccEmails,
          subject: subject.trim(),
          message: message.trim(),
          userId,
          userType,
        }
        res = await axios.post(
          `${REST_API_END_POINT}installation/send-install-reports-to-email`,
          installationPayload
        )
      } else {
        let apiUrl = (pageType === 'quotation' || pageType === 'request' || pageType === 'purchase_order' || pageType === 'product_formula' || pageType === 'design') ? 'send-invoice-pdf-to-email' : 'send-designs-to-email'
        res = await axios.post(`${REST_API_END_POINT}design/${apiUrl}`, requestPayload)
      }

      if (res.data.status === 1) {
        // Add newly typed emails to the emails list (for future To/CC options)
        const usedEmails = [recipientEmail.trim(), ...ccEmails.map((e) => e.trim())].filter(Boolean)
        const existingEmails = new Set(
          emailOptions.map((opt) => (opt && opt.email ? opt.email.toLowerCase() : ''))
        )
        const newEmails = [...new Set(usedEmails)].filter(
          (email) => validateEmail(email) && !existingEmails.has(email.toLowerCase())
        )
        if (newEmails.length > 0) {
          Promise.allSettled(
            newEmails.map((email) =>
              axios.post(`${REST_API_END_POINT}email/add-email`, { email })
            )
          ).catch(() => {})
        }

        enqueueSnackbar('Email sent successfully', { variant: 'success' })
        handleClose()
        if (onSuccess) {
          onSuccess()
        }
      } else {
        enqueueSnackbar(res.data.message || 'Failed to send email', { variant: 'error' })
      }
    } catch (error) {
      console.error('Error sending email:', error)
      enqueueSnackbar('Failed to send email', { variant: 'error' })
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = (event, reason) => {
    // Prevent closing on backdrop click
    if (reason === 'backdropClick') {
      return
    }
    if (!isSending) {
      setRecipientEmail('')
      setCcEmails([])
      setCcInputValue('')
      setSubject('')
      setMessage('')
      setEmailError('')
      setCcError('')
      setSubjectError('')
      setSenderEmail('')
      setSenderName('')
      setShowSenderField(false)
      setSelectedDownloadKeys([])
      setDownloadSelectMode(false)
      onClose()
    }
  }

  useEffect(() => {
    if (open && pageType === 'installation_report') {
      setSubject(installationName || '')
      setSubjectError('')
    }
  }, [open, pageType, installationName])

  const getFileIcon = (file) => {
    const isPdf =
      file.file_type === 'pdf' ||
      file.file_type === 'application/pdf' ||
      file.file_type === 'other' ||
      file.extension === 'pdf'
    return isPdf ? (
      <Icon icon={filePdfIcon} width={20} height={20} style={{ color: '#d32f2f' }} />
    ) : (
      <Icon icon={imageIcon} width={20} height={20} style={{ color: '#1976d2' }} />
    )
  }

  const getFileName = (file) => {
    if (pageType === 'installation_report') {
      const preferred = file.displayName || file.report_name || file.file_name
      if (preferred && !String(preferred).includes('://')) {
        return preferred
      }
    }
    if (file.name) {
      return resolveDownloadFileName(file.name) || 'Design File'
    }
    return 'Design File'
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }

  const toggleAttachmentDownloadSelect = (file, index) => {
    const key = getAttachmentKey(file, index)
    setDownloadSelectMode(true)
    setSelectedDownloadKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  const filesSelectedForDownload = (selectedDesigns || []).filter((file, index) =>
    selectedDownloadKeys.includes(getAttachmentKey(file, index)),
  )

  const triggerBase64Download = (base64, filename, contentType = 'application/pdf') => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType })
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename || 'download.pdf'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    }, 250)
  }

  const handleDownloadSelectedAttachments = async (filesToDownload = []) => {
    const list = Array.isArray(filesToDownload) ? filesToDownload : []
    if (!list.length) {
      return { successCount: 0, failCount: 0, total: 0 }
    }

    const urlFiles = list.filter((file) => getAttachmentDownloadUrl(file))
    const invoiceFiles = list.filter((file) => file?.invoiceData && !getAttachmentDownloadUrl(file))

    let successCount = 0
    let failCount = 0

    if (urlFiles.length) {
      const urlResult = await downloadAllAttachments(urlFiles)
      successCount += Number(urlResult?.successCount || 0)
      failCount += Number(urlResult?.failCount || 0)
    }

    if (invoiceFiles.length) {
      try {
        const requestPayload = {
          designs: invoiceFiles,
          downloadOnly: true,
          pageType,
          details,
          invoice,
          pdfs,
          REST_API,
          loading,
          hasViewCostPermission,
          userId,
          userType,
        }
        if ((pageType === 'request' || pageType === 'product_formula') && requestId) {
          requestPayload.request_id = requestId
        } else if (pageType !== 'quotation' && designOrderId) {
          requestPayload.design_order_id = designOrderId
        } else if (pageType === 'quotation' && quotationId) {
          requestPayload.quotation_id = quotationId
        } else if (pageType === 'purchase_order' && purchaseOrderId) {
          requestPayload.purchase_order_id = purchaseOrderId
        }

        const res = await axios.post(
          `${REST_API_END_POINT}design/send-invoice-pdf-to-email`,
          requestPayload,
        )
        const generatedFiles = res?.data?.files || []
        if (res?.data?.status === 1 && generatedFiles.length) {
          for (const file of generatedFiles) {
            try {
              triggerBase64Download(file.base64, file.filename, file.contentType)
              successCount += 1
              await new Promise((resolve) => setTimeout(resolve, 400))
            } catch (err) {
              console.error('Failed to save generated PDF:', err)
              failCount += 1
            }
          }
          // If backend returned fewer files than requested, count remaining as failed
          if (generatedFiles.length < invoiceFiles.length) {
            failCount += invoiceFiles.length - generatedFiles.length
          }
        } else {
          failCount += invoiceFiles.length
        }
      } catch (error) {
        console.error('Invoice PDF download failed:', error)
        failCount += invoiceFiles.length
      }
    }

    return {
      successCount,
      failCount,
      total: list.length,
    }
  }

  // Filter valid invoice agreements before rendering
  // const validInvoiceAgreements = (invoice?.sign_agreements || []).filter(agr => agr?.agreement_pdf_path || agr?.attachment);

  // Update the total count of attachments
  // const totalAttachments = (selectedDesigns || []).length + validInvoiceAgreements.length;
  const totalAttachments = (selectedDesigns || []).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          position: 'relative',
        },
      }}
    >
      {/* Sending Animation Overlay */}
      {isSending && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: alpha(theme.palette.background.default, 0.95),
            backdropFilter: 'blur(8px)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          {/* Animated Mail Icon */}
          <Box
            sx={{
              position: 'relative',
              mb: 3,
              '@keyframes mailFly': {
                '0%': {
                  transform: 'translateY(0) scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'translateY(-20px) scale(1.1)',
                  opacity: 0.8,
                },
                '100%': {
                  transform: 'translateY(-40px) scale(0.9)',
                  opacity: 0,
                },
              },
              '@keyframes mailPulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.15)',
                },
              },
            }}
          >
            <Box
              sx={{
                animation: 'mailPulse 2s ease-in-out infinite',
                position: 'relative',
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <MailIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Avatar>
              {/* Animated particles */}
              {[...Array(8)].map((_, i) => {
                const angle = (i * Math.PI * 2) / 8
                const distance = 50
                return (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      top: '50%',
                      left: '50%',
                      animation: 'particleFloat 2s ease-in-out infinite',
                      animationDelay: `${i * 0.15}s`,
                      '@keyframes particleFloat': {
                        '0%': {
                          transform: `translate(-50%, -50%) translate(${Math.cos(angle) * 0}px, ${Math.sin(angle) * 0}px) scale(0)`,
                          opacity: 0,
                        },
                        '50%': {
                          transform: `translate(-50%, -50%) translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(1)`,
                          opacity: 1,
                        },
                        '100%': {
                          transform: `translate(-50%, -50%) translate(${Math.cos(angle) * (distance * 1.5)}px, ${Math.sin(angle) * (distance * 1.5)}px) scale(0)`,
                          opacity: 0,
                        },
                      },
                    }}
                  />
                )
              })}
            </Box>
          </Box>

          {/* Sending Text with Animation */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 1,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 },
              },
            }}
          >
            Sending Email...
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            Your email is being sent to recipients. Please wait...
          </Typography>

          {/* Progress Indicator */}
          <Box
            sx={{
              mt: 3,
              width: 200,
              height: 4,
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '30%',
                backgroundColor: theme.palette.primary.main,
                borderRadius: 2,
                animation: 'progress 2s ease-in-out infinite',
                '@keyframes progress': {
                  '0%': { left: '-30%' },
                  '100%': { left: '100%' },
                },
              },
            }}
          />
        </Box>
      )}
      {/* Email Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: '#ffffff',
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              width: 40,
              height: 40,
            }}
          >
            <SendIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              New Message
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {pageType === 'quotation' 
                ? (isProposal
                  ? 'Send proposal via email' : 'Send estimate via email')
                : pageType === 'request'
                ? 'Send invoice via email'
                : pageType === 'purchase_order'
                ? 'Send purchase order invoice via email'
                : 'Send designs via email'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isSending}
          size="small"
          sx={{
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent 
        sx={{ 
          p: 0, 
          backgroundColor: theme.palette.background.default,
          overflow: 'auto',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          // Hide scrollbar but keep scrolling functionality
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
        }}
      >
        <Paper
          elevation={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            flex: 1,
            overflow: 'auto',
            // Hide scrollbar but keep scrolling functionality
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          }}
        >
          {/* Email Form - Gmail/Outlook Style */}
          <Box sx={{ p: 3 }}>
             {/* From (Sender) row - only for invoice emails opened from Approve button */}
             {canChangeSender && pageType === 'request' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 80,
                    pt: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  From
                </Typography>
                {showSenderField ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {/* <Autocomplete
                      freeSolo
                      options={emailOptions.filter((opt) => opt.type === 'Staff')}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option
                        return option.email || ''
                      }}
                      value={
                        senderEmail
                          ? emailOptions.find(
                              (opt) =>
                                opt.type === 'Staff' && opt.email === senderEmail
                            ) || senderEmail
                          : ''
                      }
                      onChange={(event, newValue, reason) => {
                        if (reason === 'selectOption' && newValue) {
                          const email =
                            typeof newValue === 'string' ? newValue : newValue.email
                          const name =
                            typeof newValue === 'string'
                              ? newValue
                              : newValue.label || newValue.email
                          setSenderEmail(email || '')
                          setSenderName(name || '')
                        } else if (reason === 'clear') {
                          setSenderEmail('')
                          setSenderName('')
                        }
                      }}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                          setSenderEmail(newInputValue)
                          // When manually typing, use the email itself as the name
                          setSenderName(newInputValue)
                        }
                      }}
                      loading={loadingEmails}
                      disabled={isSending}
                      disableClearable
                      clearOnBlur={false}
                      selectOnFocus={false}
                      handleHomeEndKeys={false}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                            }}
                          >
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              {option.email} • {option.type}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Sender email address"
                          variant="standard"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'new-password',
                            autoCorrect: 'off',
                            autoCapitalize: 'off',
                            spellCheck: 'false',
                            name: 'sender-email-input',
                          }}
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: loadingEmails
                              ? params.InputProps.endAdornment
                              : null,
                            sx: {
                              fontSize: '0.95rem',
                              pt: 1.5,
                            },
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: theme.palette.text.primary,
                            },
                          }}
                        />
                      )}
                      sx={{
                        flex: 1,
                        '& .MuiAutocomplete-popper': {
                          zIndex: 1300,
                        },
                      }}
                      componentsProps={{
                        popper: {
                          style: {
                            zIndex: 1300,
                          },
                        },
                      }}
                    /> */}

                    <TextField
                      fullWidth
                      placeholder="Sender email address"
                      value={senderEmail}
                      onChange={(e) => {
                        setSenderEmail(e.target.value);
                        setSenderName(e.target.value);
                      }}
                      variant="standard"
                      disabled={isSending}
                      inputProps={{
                        autoComplete: 'new-password',
                        autoCorrect: 'off',
                        autoCapitalize: 'off',
                        spellCheck: 'false',
                        name: 'sender-email-input',
                      }}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          fontSize: '0.95rem',
                          pt: 1.5,
                        },
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: theme.palette.text.primary,
                        },
                      }}
                    />


                    <Button
                      size="small"
                      onClick={() => {
                        setShowSenderField(false)
                        setSenderEmail('')
                        setSenderName('')
                      }}
                    >
                      Use default
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    {/* Default sender text (admin mail) */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      noreply@signwisesolutions.com
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setShowSenderField(true)
                        // Pre-fill with existing default admin mail
                        setSenderEmail('noreply@signwisesolutions.com')
                        setSenderName('SignWise Solutions')
                      }}
                    >
                      Send as
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {/* To Field */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  pt: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                To
              </Typography>
              <Autocomplete
                freeSolo
                openOnFocus={!!canChangeSender}
                options={emailOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option
                  return option.email || ''
                }}
                value={recipientEmail || ''}
                onChange={handleEmailChange}
                onInputChange={(event, newInputValue, reason) => {
                  // Only update on user input, not on selection
                  if (reason === 'input') {
                    setRecipientEmail(newInputValue)
                    if (newInputValue && !validateEmail(newInputValue)) {
                      setEmailError('Please enter a valid email address')
                    } else {
                      setEmailError('')
                    }
                  }
                }}
                loading={loadingEmails}
                disabled={isSending}
                disableClearable
                clearOnBlur={false}
                selectOnFocus={false}
                handleHomeEndKeys={false}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
                      <Avatar
                        src={option.profilePic || (typeof option === 'object' && option.profilePic)}
                        sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}
                      >
                        {!option.profilePic && !(typeof option === 'object' && option.profilePic)
                          ? getInitials(option.label || option.email, typeof option === 'string' ? option : option.email)
                          : null
                        }
                      </Avatar>
                      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {typeof option === 'string' ? option : option.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {typeof option === 'string' ? option : option.email} • {typeof option === 'object' ? option.type : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Recipient email address"
                    error={!!emailError}
                    helperText={emailError}
                    required
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                      autoCorrect: 'off',
                      autoCapitalize: 'off',
                      spellCheck: 'false',
                      name: 'recipient-email-input',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      endAdornment: loadingEmails ? params.InputProps.endAdornment : null,
                      sx: {
                        fontSize: '0.95rem',
                        pt: 1.5,
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiFormHelperText-root': {
                        marginLeft: 0,
                        marginTop: 0.5,
                      },
                    }}
                  />
                )}
                sx={{
                  flex: 1,
                  '& .MuiAutocomplete-popper': {
                    zIndex: 1300,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 1300,
                    },
                  },
                }}
              />
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  pt: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                To
              </Typography>
              <Autocomplete
                freeSolo
                options={emailOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option
                  return option.email || ''
                }}
                value={recipientEmail || ''}
                onChange={handleEmailChange}
                onInputChange={(event, newInputValue, reason) => {
                  // Only update on user input, not on selection
                  if (reason === 'input') {
                    setRecipientEmail(newInputValue)
                    if (newInputValue && !validateEmail(newInputValue)) {
                      setEmailError('Please enter a valid email address')
                    } else {
                      setEmailError('')
                    }
                  }
                }}
                loading={loadingEmails}
                disabled={isSending}
                disableClearable
                clearOnBlur={false}
                selectOnFocus={false}
                handleHomeEndKeys={false}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
                            <Avatar
                              src={option.profilePic}
                              sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}
                            >
                              {!option.profilePic 
                                ? getInitials(option.label || option.email, option.email)
                                : null
                              }
                            </Avatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {option.email} • {option.type}
                              </Typography>
                            </Box>
                          </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Recipient email address"
                    error={!!emailError}
                    helperText={emailError}
                    required
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                      autoCorrect: 'off',
                      autoCapitalize: 'off',
                      spellCheck: 'false',
                      name: 'recipient-email-input',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      endAdornment: loadingEmails ? params.InputProps.endAdornment : null,
                      sx: {
                        fontSize: '0.95rem',
                        pt: 1.5,
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiFormHelperText-root': {
                        marginLeft: 0,
                        marginTop: 0.5,
                      },
                    }}
                  />
                )}
                sx={{ 
                  flex: 1,
                  '& .MuiAutocomplete-popper': {
                    zIndex: 1300,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 1300,
                    },
                  },
                }}
              />
            </Box> */}

            {/* CC Field */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5,
                minHeight: 60,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  pt: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Cc
              </Typography>
              <Box 
                sx={{ 
                  flex: 1, 
                  pt: 0.5,
                  cursor: 'text',
                  width: '100%',
                  minWidth: 0,
                }}
                onClick={(e) => {
                  // Focus the input when clicking anywhere in the CC field area
                  const input = e.currentTarget.querySelector('input')
                  if (input && !isSending) {
                    input.focus()
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'flex-start',
                    alignContent: 'flex-start',
                    minHeight: 40,
                    width: '100%',
                    pb: 0.5,
                  }}
                >
                  {ccEmails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      size="small"
                      onDelete={() => removeCcEmail(index)}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        '& .MuiChip-deleteIcon': {
                          color: theme.palette.primary.main,
                          '&:hover': {
                            color: theme.palette.primary.dark,
                          },
                        },
                      }}
                    />
                  ))}
                  <Autocomplete
                    freeSolo
                    options={emailOptions}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option
                      return option.email || ''
                    }}
                    inputValue={ccInputValue || ''}
                    onInputChange={(event, newInputValue, reason) => {
                      // Only update on user input, not on selection or reset
                      if (reason === 'input') {
                        setCcInputValue(newInputValue)
                        if (ccError) {
                          setCcError('')
                        }
                      } else if (reason === 'reset' || reason === 'clear') {
                        // Clear input when reset or cleared
                        setCcInputValue('')
                      }
                    }}
                    onChange={(event, newValue, reason) => {
                      // Only handle when an option is selected, not on input
                      if (reason === 'selectOption' && newValue) {
                        if (event) {
                          event.preventDefault()
                          event.stopPropagation()
                        }
                        const email = typeof newValue === 'string' ? newValue : newValue.email
                        if (email) {
                          // Add email to CC list first
                          addCcEmail(email)
                          // Clear input immediately after
                          setCcInputValue('')
                        }
                      } else if (reason === 'clear') {
                        setCcInputValue('')
                      }
                    }}
                    onKeyDown={handleCcInputKeyDown}
                    onBlur={handleCcInputBlur}
                    loading={loadingEmails}
                    disabled={isSending}
                    disableClearable
                    clearOnBlur={false}
                    selectOnFocus={false}
                    handleHomeEndKeys={false}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
                          <Avatar
                            src={option.profilePic || (typeof option === 'object' && option.profilePic)}
                            sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}
                          >
                            {!option.profilePic && !(typeof option === 'object' && option.profilePic) 
                              ? getInitials(option.label || option.email, typeof option === 'string' ? option : option.email)
                              : null
                            }
                          </Avatar>
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {typeof option === 'string' ? option : option.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                              {typeof option === 'string' ? option : option.email} • {typeof option === 'object' ? option.type : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Cc email addresses"
                        error={!!ccError}
                        helperText={ccError}
                        variant="standard"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password',
                          autoCorrect: 'off',
                          autoCapitalize: 'off',
                          spellCheck: 'false',
                          name: 'cc-email-input',
                        }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                          endAdornment: loadingEmails ? params.InputProps.endAdornment : null,
                          sx: {
                            fontSize: '0.95rem',
                          },
                        }}
                        sx={{
                          flex: '1 1 auto',
                          minWidth: 200,
                          maxWidth: '100%',
                          '& .MuiInputBase-root': {
                            width: '100%',
                          },
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                            width: '100%',
                          },
                          '& .MuiFormHelperText-root': {
                            marginLeft: 0,
                            marginTop: 0.5,
                          },
                        }}
                      />
                    )}
                    sx={{ 
                      flex: '1 1 auto',
                      minWidth: 200,
                      maxWidth: '100%',
                      '& .MuiAutocomplete-popper': {
                        zIndex: 1300,
                      },
                    }}
                    componentsProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                              altBoundary: true,
                              rootBoundary: 'viewport',
                              padding: 8,
                            },
                          },
                          {
                            name: 'flip',
                            enabled: true,
                            options: {
                              altBoundary: true,
                              rootBoundary: 'viewport',
                              padding: 8,
                            },
                          },
                          {
                            name: 'offset',
                            enabled: true,
                            options: {
                              offset: [0, 4],
                            },
                          },
                        ],
                        style: {
                          zIndex: 1300,
                        },
                      },
                    }}
                    disablePortal={false}
                    ListboxProps={{
                      style: {
                        maxHeight: '300px',
                        overflow: 'auto',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Subject Field */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  pt: 1.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Subject
              </Typography>
              <TextField
                fullWidth
                placeholder="Email subject"
                value={subject}
                onChange={handleSubjectChange}
                error={!!subjectError}
                helperText={subjectError}
                required
                disabled={isSending}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.95rem',
                    pt: 1.5,
                  },
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    marginTop: 0.5,
                  },
                }}
              />
            </Box>

            {/* Attachments Section */}
            {/* Attachments Section */}
            {((selectedDesigns || []).length > 0) && (
            // {(selectedDesigns.length > 0 || validInvoiceAgreements.length > 0) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  py: 1.5,
                  backgroundColor: alpha(theme.palette.info.main, 0.05),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                  },
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    Attachments
                  </Typography>
                  <AttachmentDownloadAllButton
                    files={filesSelectedForDownload}
                    allFiles={selectedDesigns || []}
                    onToggleSelectAll={(checked) => {
                      setDownloadSelectMode(true)
                      setSelectedDownloadKeys(checked ? [...attachmentKeys] : [])
                    }}
                    onEmptyClick={() => {
                      setDownloadSelectMode(true)
                      enqueueSnackbar(
                        'Click attachments to select, then press Download Selected again',
                        { variant: 'info' },
                      )
                    }}
                    onDownloadComplete={() => {
                      setSelectedDownloadKeys([])
                      setDownloadSelectMode(false)
                    }}
                    onDownload={handleDownloadSelectedAttachments}
                    isFileDownloadable={isAttachmentDownloadable}
                    selectionMode
                    label="Download Selected"
                    size="small"
                  />
                </Box>
                <Box sx={{ width: '100%', overflow: 'hidden', px: 1.5 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 1.5,
                      mb: 1.5,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {selectedDesigns.map((file, index) => {
                      const fileKey = getAttachmentKey(file, index)
                      const isSelected = selectedDownloadKeys.includes(fileKey)
                      return (
                      <Paper
                        key={fileKey}
                        elevation={0}
                        onClick={() => toggleAttachmentDownloadSelect(file, index)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: isSelected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : theme.palette.background.paper,
                          border: `1px solid ${
                            isSelected
                              ? theme.palette.primary.main
                              : theme.palette.divider
                          }`,
                          transition: 'all 0.2s ease-in-out',
                          minHeight: 72,
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                          },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleAttachmentDownloadSelect(file, index)}
                          sx={{ p: 0.25, flexShrink: 0 }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            flexShrink: 0,
                          }}
                        >
                          {getFileIcon(file)}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden', width: '100%' }}>
                          <Tooltip title={getFileName(file)}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: theme.palette.text.primary,
                              mb: 0.25,
                              width: '100%',
                            }}
                          >
                            {getFileName(file)}
                          </Typography>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.7rem',
                            }}
                          >
                            {formatFileSize(file.size || file.file_size)}
                          </Typography>
                        </Box>
                      </Paper>
                      )
                    })}
                    {/* {validInvoiceAgreements.map((agr, idx) => (
                      <Paper
                        key={`agr-${idx}`}
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.2s ease-in-out',
                          minHeight: 72,
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            flexShrink: 0,
                          }}
                        >
                          <PictureAsPdf sx={{ color: theme.palette.error.main }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden', width: '100%' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: theme.palette.text.primary,
                              mb: 0.25,
                              width: '100%',
                            }}
                          >
                            {agr.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.7rem',
                            }}
                          >
                            Sign Agreement
                          </Typography>
                        </Box>
                      </Paper>
                    ))} */}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {totalAttachments} file{totalAttachments !== 1 ? 's' : ''} attached
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Message Body */}
            <Box sx={{ mt: 2, minHeight: 200 }}>
              <TextField
                fullWidth
                placeholder="Compose your message here..."
                multiline
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSending}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.95rem',
                    pt: 1,
                  },
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                    lineHeight: 1.6,
                  },
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      {/* Email Footer Actions */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Badge badgeContent={(selectedDesigns || []).length} color="primary">
          {/* <Badge badgeContent={selectedDesigns.length + ((details?.sign_agreements && details.sign_agreements.length) || (invoice?.sign_agreements && invoice.sign_agreements.length) || 0)} color="primary"> */}
            <Tooltip title="Attachments">
              <IconButton
                size="small"
                disabled
                sx={{
                  color: theme.palette.text.secondary,
                  cursor: 'default',
                }}
              >
                <AttachFile />
              </IconButton>
            </Tooltip>
          </Badge>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {(selectedDesigns || []).length} attachment{(selectedDesigns || []).length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            onClick={handleClose}
            disabled={isSending}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              },
            }}
          >
            Discard
          </Button>
          <LoadingButton
            onClick={handleSend}
            variant="contained"
            color="primary"
            loading={isSending}
            startIcon={<SendIcon />}
            sx={{
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            Send
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

